import { useEffect, useRef, useState, type RefObject } from 'react';

interface QrReader {
  detect(source: HTMLVideoElement): Promise<Array<{ rawValue: string }>>;
}

interface BarcodeDetectorClass {
  new (options: { formats: string[] }): QrReader;
  getSupportedFormats(): Promise<readonly string[]>;
}

/** How often a frame is read: quick enough to feel instant, light on a phone. */
const EVERY_MS = 250;

let reader: Promise<QrReader> | null = null;

/**
 * The browser's own BarcodeDetector where it reads QR codes (Chrome on
 * Android), and otherwise the same API in WebAssembly (Safari has none).
 * The fallback is loaded the first time a scan opens, not with the app, and
 * fetches its reader from jsDelivr as the old shell's scanner did.
 */
function qrReader(): Promise<QrReader> {
  reader ??= (async () => {
    const Native = (window as { BarcodeDetector?: BarcodeDetectorClass })
      .BarcodeDetector;
    if (Native && (await Native.getSupportedFormats()).includes('qr_code'))
      return new Native({ formats: ['qr_code'] });
    const { BarcodeDetector, prepareZXingModule } = await import(
      'barcode-detector/ponyfill'
    );
    // Fetch the WebAssembly now, so a reader that cannot load says so
    // instead of failing quietly on every frame.
    await prepareZXingModule({ fireImmediately: true });
    return new BarcodeDetector({ formats: ['qr_code'] });
  })().catch((error: unknown) => {
    // Offline when it first loaded: the next scan tries again.
    reader = null;
    throw error;
  });
  return reader;
}

/**
 * Read a QR code off a live camera feed (the library's CameraFeed), frame
 * by frame, and hand over the first code it finds, once. `live` is whether
 * the feed is running; nothing is read before it is or after it stops.
 * `failed` is the reader itself not loading, which the screen says, rather
 * than a camera that seems to see nothing.
 */
export function useQrScan(
  video: RefObject<HTMLVideoElement>,
  onCode: (code: string) => void,
  live: boolean
): { failed: boolean } {
  const report = useRef(onCode);
  report.current = onCode;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!live) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const read = async () => {
      let qr: QrReader;
      try {
        qr = await qrReader();
      } catch {
        if (!stopped) setFailed(true);
        return;
      }
      const element = video.current;
      // HAVE_CURRENT_DATA: there is a frame to read.
      if (!stopped && element && element.readyState >= 2) {
        try {
          const [found] = await qr.detect(element);
          if (!stopped && found?.rawValue) {
            stopped = true;
            report.current(found.rawValue);
            return;
          }
        } catch {
          // A frame that would not read; the next one usually does.
        }
      }
      if (!stopped) timer = setTimeout(read, EVERY_MS);
    };
    void read();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [live, video]);

  return { failed };
}

/** Forget the reader, between tests. */
export function _resetQrReader(): void {
  reader = null;
}
